import { FC, FormEvent, useEffect, useRef, useState } from 'react';

import { useGetPolicySettingsQuery } from '@entities/policy';
import {
  IEditorPolicy,
  IEditorPolicyCondition,
  IEditorPolicyInput,
} from '@entities/routing/config-version';
import AddIcon from '@mui/icons-material/Add';
import { Stack, Typography } from '@mui/material';
import { FormItem } from '@shared/components/Form';
import { useModal } from '@shared/components/Modal';

import PolicyConditionGroup, { TConditionDimension } from './PolicyConditionGroup';

interface IProps {
  policy?: Pick<IEditorPolicy, 'name' | 'description' | 'conditions'>;
  onSubmit: (input: IEditorPolicyInput) => void;
}

// Stable keys: removing a group must not re-seed its neighbours' selects.
interface IConditionGroup {
  key: number;
  condition: IEditorPolicyCondition;
}

const EMPTY_HINT = 'Leave a field empty to match any value for it.';

const emptyCondition = (): IEditorPolicyCondition => ({
  currency: [],
  method: [],
  traffic: [],
  merchants: [],
});

// The Policies page's PolicyForm on the editor types; local only — no requests.
const PolicyEditorForm: FC<IProps> = ({ policy, onSubmit }) => {
  const { closeModal } = useModal();
  const { data: settings } = useGetPolicySettingsQuery();
  const [name, setName] = useState(policy?.name ?? '');
  const [description, setDescription] = useState(policy?.description ?? '');
  const [groups, setGroups] = useState<IConditionGroup[]>(() =>
    (policy?.conditions?.length ? policy.conditions : [emptyCondition()]).map((condition, key) => ({
      key,
      condition,
    })),
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const nextKeyRef = useRef(groups.length);
  const nameRef = useRef<HTMLInputElement>(null);

  const isEdit = policy !== undefined;

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const updateDimension = (key: number, dimension: TConditionDimension, values: string[]) => {
    setGroups(prev =>
      prev.map(group =>
        group.key === key
          ? { ...group, condition: { ...group.condition, [dimension]: values } }
          : group,
      ),
    );
  };

  const addGroup = () => {
    setGroups(prev => [...prev, { key: nextKeyRef.current++, condition: emptyCondition() }]);
  };

  const removeGroup = (key: number) => {
    setGroups(prev => prev.filter(group => group.key !== key));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();

    if (!trimmed) {
      setNameError('Name is required.');
      nameRef.current?.focus();
      return;
    }

    onSubmit({
      name: trimmed,
      description: description.trim() || null,
      conditions: groups.map(group => group.condition),
    });
    closeModal();
  };

  return (
    <form className='modal-sticky-layout routing-policy-form' onSubmit={handleSubmit} noValidate>
      <div className='modal-sticky-header'>
        <Typography variant='h6'>{isEdit ? 'Edit policy' : 'New policy'}</Typography>
      </div>

      <div className='modal-sticky-content'>
        <Stack spacing={2}>
          {/* FormItemWithError markup with a local error */}
          <div className={`form-item ${nameError ? 'has-error' : ''}`}>
            <label htmlFor='routing-policy-name'>Name</label>
            <div className='form-field'>
              <input
                id='routing-policy-name'
                ref={nameRef}
                type='text'
                placeholder='Name'
                value={name}
                aria-invalid={nameError !== null}
                onChange={event => {
                  setName(event.target.value);
                  setNameError(null);
                }}
              />
            </div>
            <div className='form-error' role={nameError ? 'alert' : undefined}>
              {nameError}
            </div>
          </div>

          <FormItem name='routing-policy-description' label='Description'>
            <textarea
              id='routing-policy-description'
              placeholder='Description'
              value={description}
              onChange={event => setDescription(event.target.value)}
            />
          </FormItem>

          <Stack spacing={2}>
            <div>
              <Typography variant='subtitle1' fontWeight={700}>
                Traffic & Merchants Conditions
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {EMPTY_HINT}
              </Typography>
            </div>

            {groups.map((group, index) => (
              <PolicyConditionGroup
                key={group.key}
                index={index}
                condition={group.condition}
                dictionaries={settings?.data}
                canRemove={groups.length > 1}
                onChange={(dimension, values) => updateDimension(group.key, dimension, values)}
                onRemove={() => removeGroup(group.key)}
              />
            ))}

            <button type='button' className='secondary-btn' onClick={addGroup}>
              <AddIcon fontSize='small' /> Add Traffic Condition
            </button>
          </Stack>
        </Stack>
      </div>

      <div className='modal-sticky-footer'>
        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <button type='button' className='secondary-btn' onClick={closeModal}>
            Cancel
          </button>
          <button type='submit' className='primary-btn'>
            {isEdit ? 'Save changes' : 'Create policy'}
          </button>
        </Stack>
      </div>
    </form>
  );
};

export default PolicyEditorForm;
